import React, { useState } from 'react';
import {
    IconButton,
    Typography,
    makeStyles,
    Box,
    Modal,
    Button,
    MenuItem,
    Divider,
    Grid,
} from '@material-ui/core';
import { ChevronLeft, ChevronRight, CalendarToday } from '@material-ui/icons';
import moment, { Moment } from 'moment';

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    iconButton: {
        border: `1px solid #4D4AB5`,
        borderRadius: 8,
        backgroundColor: '#F1F3F6',
        '&:hover': {
            backgroundColor: theme.palette.grey[200],
        },
    },
    dateText: {
        minWidth: 111,
        textAlign: 'center',
        fontWeight: 600,
        color: '#4D4AB5',
    },
    modalPaper: {
        position: 'absolute' as const,
        top: '30%',
        left: '50%',
        transform: 'translate(-50%, -30%)',
        backgroundColor: 'white',
        padding: theme.spacing(4),
        outline: 'none',
        borderRadius: 10,
        boxShadow: theme.shadows[5],
        minWidth: 300,
    },
    inputBox: {
        display: 'flex',
        gap: theme.spacing(2),
        marginTop: theme.spacing(2),
        justifyContent: 'center',
    },
    presetButton: {
        width: '100%',
        justifyContent: 'flex-start',
        textTransform: 'none',
        padding: theme.spacing(1, 2),
    },
}));

// Output structure
export interface DateRange {
    startDate: Moment;
    endDate: Moment;
    formattedRange: string;
    numberOfDays: number;
    isValidRange: boolean;
}

// Props
interface DateRangePickerProps {
    initialStartDate?: Moment;
    initialEndDate?: Moment;
    disableManualInput?: boolean;
    onChange?: (range: DateRange) => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
    initialStartDate = moment().startOf('week'),
    initialEndDate = moment().endOf('week'),
    disableManualInput = false,
    onChange,
}) => {
    const classes = useStyles();
    const [startDate, setStartDate] = useState<Moment>(initialStartDate);
    const [endDate, setEndDate] = useState<Moment>(initialEndDate);
    const [modalOpen, setModalOpen] = useState(false);

    const updateRange = (start: Moment, end: Moment) => {
        const isValid =
            start.isValid() && end.isValid() && start.isSameOrBefore(end);

        if (!isValid) {
            alert('Invalid date range');
            return;
        }

        setStartDate(start);
        setEndDate(end);

        onChange?.({
            startDate: start,
            endDate: end,
            formattedRange: `${start.format('DD MMM')} - ${end.format(
                'DD MMM',
            )}`,
            numberOfDays: end.diff(start, 'days') + 1,
            isValidRange: isValid,
        });
    };

    const handleRangeChange = (direction: 'prev' | 'next') => {
        const diff = endDate.diff(startDate, 'days') + 1;
        const offset = direction === 'prev' ? -diff : diff;

        const newStart = startDate.clone().add(offset, 'days');
        const newEnd = endDate.clone().add(offset, 'days');

        updateRange(newStart, newEnd);
    };

    const handlePreset = (label: string) => {
        const today = moment();
        switch (label) {
            case 'Today':
                updateRange(today, today);
                break;
            case 'This Week':
                updateRange(moment().startOf('week'), moment().endOf('week'));
                break;
            case 'Last 7 Days':
                updateRange(moment().subtract(6, 'days'), today);
                break;
            case 'This Month':
                updateRange(moment().startOf('month'), moment().endOf('month'));
                break;
            default:
                break;
        }
        setModalOpen(false);
    };

    const handleManualApply = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const form = e.currentTarget;
        const startInput = (
            form.elements.namedItem('start') as HTMLInputElement
        ).value;
        const endInput = (form.elements.namedItem('end') as HTMLInputElement)
            .value;

        const start = moment(startInput);
        const end = moment(endInput);

        updateRange(start, end);
        setModalOpen(false);
    };

    return (
        <>
            <Box className={classes.container}>
                <IconButton
                    className={classes.iconButton}
                    onClick={() => handleRangeChange('prev')}
                >
                    <ChevronLeft />
                </IconButton>

                <Typography className={classes.dateText}>
                    {startDate.format('DD MMM')} - {endDate.format('DD MMM')}
                </Typography>

                <IconButton
                    className={classes.iconButton}
                    onClick={() => handleRangeChange('next')}
                >
                    <ChevronRight />
                </IconButton>

                <IconButton
                    className={classes.iconButton}
                    onClick={() => setModalOpen(true)}
                >
                    <CalendarToday color="primary" />
                </IconButton>
            </Box>

            <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
                <div className={classes.modalPaper}>
                    <Typography variant="h6">Select Date Range</Typography>

                    <Box mt={2}>
                        {[
                            'Today',
                            'This Week',
                            'Last 7 Days',
                            'This Month',
                        ].map((preset) => (
                            <MenuItem
                                key={preset}
                                onClick={() => handlePreset(preset)}
                                className={classes.presetButton}
                            >
                                {preset}
                            </MenuItem>
                        ))}
                    </Box>

                    {!disableManualInput && (
                        <form onSubmit={handleManualApply}>
                            <Divider style={{ margin: '16px 0' }} />
                            <div className={classes.inputBox}>
                                <input
                                    name="start"
                                    type="date"
                                    defaultValue={startDate.format(
                                        'YYYY-MM-DD',
                                    )}
                                    required
                                />
                                <input
                                    name="end"
                                    type="date"
                                    defaultValue={endDate.format('YYYY-MM-DD')}
                                    required
                                />
                            </div>

                            <Box
                                mt={3}
                                display="flex"
                                justifyContent="flex-end"
                            >
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                >
                                    Apply
                                </Button>
                                <Button
                                    onClick={() => setModalOpen(false)}
                                    style={{ marginLeft: 10 }}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </form>
                    )}
                </div>
            </Modal>
        </>
    );
};

export default DateRangePicker;
